import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarX, ArrowDownToLine, ShieldCheck, Plug, BrainCircuit, Trophy, Users, Quote, Terminal, Activity, ChevronRight } from 'lucide-react';
export function LandingPage() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const fadeChild = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <div className="relative w-full overflow-hidden bg-[#0a0a0a]" data-id="element-259">
      <div className="absolute inset-0 bg-noise z-0" data-id="element-260"></div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
      backgroundImage: 'linear-gradient(rgba(245,158,11,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.3) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-261"></div>

      {/* Hero ambient glow */}
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-centurion-500/8 rounded-full blur-[150px] pointer-events-none z-0" data-id="element-262"></div>

      {/* ===== HERO ===== */}
      <section className="relative z-10 pt-32 pb-20 md:pt-44 md:pb-28 px-6 max-w-5xl mx-auto" data-id="element-263">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} data-id="element-264">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded bg-surface-400/50 border border-surface-200 text-xs font-mono text-gray-500 uppercase tracking-widest mb-10" data-id="element-265">
            <Terminal size={14} className="text-centurion-500" data-id="element-266" />
            <span data-id="element-267">Agentic Health OS</span>
            <span className="text-centurion-500" data-id="element-268">v1.0</span>
          </div>

          <h1 className="font-mono text-4xl md:text-6xl lg:text-7xl font-bold text-gray-100 leading-[1.1] mb-8 tracking-tighter uppercase" data-id="element-269">
            Your Wearables Track
            <br data-id="element-270" />
            the Past.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-centurion-400 to-amber-600" data-id="element-271">
              Our Agent
            </span>
            <br data-id="element-272" />
            Orchestrates Your Future.
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mb-10 font-light leading-relaxed" data-id="element-273">
            Most tech leaders are data-rich and adherence-poor.{' '}
            <span className="text-gray-200 font-medium" data-id="element-274">habbit.at</span> is the
            first Agentic OS that closes the loop between your calendar and your
            biometrics. We don't just track the streak — we automate the
            execution.
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-4 mb-8" data-id="element-275">
            <Link to="/app" className="w-full sm:w-auto bg-centurion-500 hover:bg-centurion-400 text-surface-600 px-8 py-4 rounded text-lg font-mono font-bold tracking-wide transition-all flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(245,158,11,0.2)] hover:shadow-[0_0_40px_rgba(245,158,11,0.4)]" data-id="element-276">
              Initialize the Executive Beta <ArrowRight size={20} data-id="element-277" />
            </Link>
          </div>

          <p className="text-sm text-gray-500 font-mono leading-relaxed" data-id="element-278">
            <span className="text-gray-400" data-id="element-279">Optimized for</span> Whoop, Fittr,
            Garmin, and Outlook.{' '}
            <span className="text-centurion-500/80" data-id="element-280">
              Limited to 50 Director+ leaders in Bengaluru.
            </span>
          </p>
        </motion.div>
      </section>

      {/* ===== THE ADHERENCE GAP ===== */}
      <section className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-281">
        <div className="max-w-4xl mx-auto px-6" data-id="element-282">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} data-id="element-283">
            <span className="text-xs font-mono font-bold text-centurion-500 uppercase tracking-[0.25em] mb-6 block" data-id="element-284">
              The Adherence Gap
            </span>

            <h2 className="font-mono text-3xl md:text-5xl font-bold text-gray-100 mb-8 uppercase tracking-tight leading-tight" data-id="element-285">
              Your calendar is the enemy
              <br data-id="element-286" />
              of your consistency.
            </h2>

            <div className="bg-surface-400/60 border border-surface-200 rounded-xl p-8 md:p-10 relative overflow-hidden" data-id="element-287">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-centurion-500/40 to-transparent" data-id="element-288"></div>

              <p className="text-gray-400 leading-relaxed text-lg" data-id="element-289">
                You have the{' '}
                <span className="text-gray-200 font-medium" data-id="element-290">$500 sensors</span>{' '}
                and the ambition. But when a 7 PM US-sync drops or a deployment
                fails, your "100-day streak" is the first casualty. Standard
                apps{' '}
                <span className="text-red-400/80 font-medium" data-id="element-291">
                  punish your misses
                </span>
                . habbit.at treats your health like a{' '}
                <span className="text-centurion-400 font-mono font-medium" data-id="element-292">
                  mission-critical system
                </span>
                . We don't punish the failure; we{' '}
                <span className="text-emerald-400 font-medium" data-id="element-293">
                  auto-repair the protocol
                </span>
                .
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== THE COMMANDER ENGINE ===== */}
      <section id="protocol" className="relative z-10 py-24 max-w-6xl mx-auto px-6" data-id="element-294">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="mb-16" data-id="element-295">
          <span className="text-xs font-mono font-bold text-centurion-500 uppercase tracking-[0.25em] mb-4 block" data-id="element-296">
            The Commander Engine
          </span>
          <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 uppercase tracking-tight" data-id="element-297">
            Zero-friction execution.
          </h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-298">
          {[{
          icon: CalendarX,
          title: 'Proactive Re-Scheduling',
          body: 'Meeting ran long? The Agent detects the conflict in your Outlook and stages a "Maintenance Protocol" instantly. No decisions. No friction. Just execution.'
        }, {
          icon: ArrowDownToLine,
          title: 'The Protocol Down-Shift',
          body: 'When time is scarce, the Agent scales the session length — never the intensity. We protect the win by shrinking the window. Partial completion beats a default to zero.'
        }, {
          icon: ShieldCheck,
          title: 'Executive Governance',
          body: 'Zero-loss architecture. If the 60-min gym window dies, the 12-min "Micro-Protocol" takes its place. Maintain 100% adherence without a single manual edit.'
        }].map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={i} variants={fadeChild} className="bg-surface-400/50 border border-surface-200 rounded-xl p-8 relative overflow-hidden group hover:border-centurion-500/30 transition-colors" data-id="element-299">
                <div className="absolute top-0 left-0 w-full h-px bg-surface-200 group-hover:bg-centurion-500/50 transition-colors" data-id="element-300"></div>
                <div className="w-12 h-12 rounded-lg bg-surface-300 border border-surface-200 flex items-center justify-center mb-6 group-hover:border-centurion-500/40 transition-colors" data-id="element-301">
                  <Icon size={22} className="text-centurion-500" data-id="element-302" />
                </div>
                <h4 className="text-lg font-bold text-gray-100 mb-4 font-mono uppercase tracking-wide" data-id="element-303">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-400 leading-relaxed" data-id="element-304">
                  {item.body}
                </p>
              </motion.div>;
        })}
        </motion.div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-305">
        <div className="max-w-6xl mx-auto px-6" data-id="element-306">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="mb-20" data-id="element-307">
            <span className="text-xs font-mono font-bold text-centurion-500 uppercase tracking-[0.25em] mb-4 block" data-id="element-308">
              3-Step Integration
            </span>
            <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 uppercase tracking-tight" data-id="element-309">
              From ingest to victory.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-310">
            {[{
            step: '01',
            title: 'Deep Ingest',
            icon: Plug,
            desc: 'One-click sync with Strava, Apple Health, and your Work Calendar. We see the 14-hour day before you feel it.'
          }, {
            step: '02',
            title: 'Agentic Orchestration',
            icon: BrainCircuit,
            desc: 'The system identifies "High-Stress" windows and proactively proposes shifts to your health blocks. Accept to auto-block your calendar.'
          }, {
            step: '03',
            title: 'Automated Victory',
            icon: Trophy,
            desc: 'Execute the command. The Agent logs the win. Your "Invincibility Score" remains intact, regardless of the chaos.'
          }].map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.12
            }} className="relative group" data-id="element-311">
                  <div className="text-8xl font-mono-num font-bold text-surface-200 absolute -top-12 -left-4 z-0 select-none opacity-40 group-hover:opacity-80 group-hover:text-centurion-500/20 transition-all" data-id="element-312">
                    {item.step}
                  </div>
                  <div className="relative z-10" data-id="element-313">
                    <div className="w-14 h-14 rounded-lg bg-surface-300 border border-surface-200 flex items-center justify-center mb-6 group-hover:border-centurion-500/50 transition-colors" data-id="element-314">
                      <Icon size={24} className="text-centurion-500" data-id="element-315" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-100 mb-3 font-mono uppercase tracking-tight" data-id="element-316">
                      {item.title}
                    </h4>
                    <p className="text-gray-400 leading-relaxed" data-id="element-317">{item.desc}</p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== PEER BOARD ===== */}
      <section id="peer-board" className="relative z-10 py-24 max-w-6xl mx-auto px-6" data-id="element-318">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-319">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} data-id="element-320">
            <span className="text-xs font-mono font-bold text-centurion-500 uppercase tracking-[0.25em] mb-4 block" data-id="element-321">
              The Governance Layer
            </span>
            <h2 className="font-mono text-3xl md:text-4xl font-bold text-gray-100 mb-6 uppercase tracking-tight leading-tight" data-id="element-322">
              High-Status
              <br data-id="element-323" />
              <span className="text-centurion-500" data-id="element-324">Accountability.</span>
            </h2>
            <p className="text-gray-400 leading-relaxed text-lg mb-8" data-id="element-325">
              You aren't just tracking for yourself. You are reporting to a
              private "Board of Directors" — a curated group of 5 peer-level
              leaders.
            </p>
            <div className="bg-surface-400/60 border border-surface-200 rounded-xl p-6 relative overflow-hidden" data-id="element-326">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-centurion-500/40 to-transparent" data-id="element-327"></div>
              <div className="flex items-center gap-3 mb-4" data-id="element-328">
                <Activity size={18} className="text-centurion-500" data-id="element-329" />
                <span className="font-mono text-sm font-bold text-gray-200 uppercase tracking-wider" data-id="element-330">
                  The Metric: AUS
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-331">
                <span className="text-gray-200 font-medium" data-id="element-332">
                  Adherence Under Stress
                </span>{' '}
                — We track how often you hit your protocol when your calendar
                was &gt;80% full. This isn't a gym leaderboard; it's a status
                report on your{' '}
                <span className="text-centurion-400 font-mono" data-id="element-333">
                  systemic reliability
                </span>
                .
              </p>
            </div>
          </motion.div>

          {/* Mock Peer Board UI */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-surface-400/40 border border-surface-200 rounded-2xl p-6 shadow-2xl backdrop-blur-sm relative" data-id="element-334">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-centurion-500/20 via-centurion-500 to-centurion-500/20 rounded-t-2xl" data-id="element-335"></div>

            <div className="flex items-center gap-3 border-b border-surface-200 pb-4 mb-5" data-id="element-336">
              <Users size={16} className="text-centurion-400" data-id="element-337" />
              <span className="text-gray-200 font-bold font-mono text-sm uppercase tracking-wide" data-id="element-338">
                Peer Board — Q1 2026
              </span>
            </div>

            <div className="space-y-3" data-id="element-339">
              {[{
              name: 'A. Gorti',
              role: 'VP Eng',
              aus: '94%',
              streak: 47,
              highlight: true
            }, {
              name: 'R. Mehta',
              role: 'Dir. Product',
              aus: '91%',
              streak: 38,
              highlight: false
            }, {
              name: 'S. Krishnan',
              role: 'CTO',
              aus: '88%',
              streak: 52,
              highlight: false
            }, {
              name: 'P. Sharma',
              role: 'SVP Ops',
              aus: '85%',
              streak: 29,
              highlight: false
            }, {
              name: 'K. Reddy',
              role: 'Dir. Eng',
              aus: '82%',
              streak: 41,
              highlight: false
            }].map((member, i) => <div key={i} className={`flex items-center justify-between h-14 rounded-lg px-4 border transition-colors ${member.highlight ? 'bg-centurion-900/20 border-centurion-500/30' : 'bg-surface-300/80 border-surface-200'}`} data-id="element-340">
                  <div className="flex items-center gap-3" data-id="element-341">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono font-bold ${member.highlight ? 'bg-centurion-500/20 text-centurion-400' : 'bg-surface-200 text-gray-400'}`} data-id="element-342">
                      {i + 1}
                    </div>
                    <div data-id="element-343">
                      <span className={`text-sm font-medium block ${member.highlight ? 'text-centurion-400' : 'text-gray-200'}`} data-id="element-344">
                        {member.name}
                      </span>
                      <span className="text-[10px] text-gray-500 font-mono uppercase" data-id="element-345">
                        {member.role}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-346">
                    <div className="text-right" data-id="element-347">
                      <span className={`text-sm font-mono-num font-bold block ${member.highlight ? 'text-centurion-400' : 'text-gray-300'}`} data-id="element-348">
                        {member.aus}
                      </span>
                      <span className="text-[10px] text-gray-500 font-mono" data-id="element-349">
                        AUS
                      </span>
                    </div>
                    <div className="text-right hidden sm:block" data-id="element-350">
                      <span className="text-sm font-mono-num font-bold text-gray-400 block" data-id="element-351">
                        {member.streak}d
                      </span>
                      <span className="text-[10px] text-gray-500 font-mono" data-id="element-352">
                        STREAK
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOUNDER-FIT QUOTE ===== */}
      <section className="relative z-10 py-24 bg-surface-500/20 border-y border-surface-200/30" data-id="element-353">
        <div className="max-w-4xl mx-auto px-6" data-id="element-354">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="relative" data-id="element-355">
            <Quote size={48} className="text-centurion-500/20 absolute -top-2 -left-2 md:-left-8" data-id="element-356" />
            <blockquote className="relative z-10 pl-6 md:pl-12 border-l-2 border-centurion-500/40" data-id="element-357">
              <p className="text-xl md:text-2xl text-gray-200 leading-relaxed font-light mb-8" data-id="element-358">
                "Built for the{' '}
                <span className="text-centurion-400 font-medium" data-id="element-359">
                  'Always-On'
                </span>{' '}
                leader who treats their health like a P&L. We've removed the
                decision-fatigue of habit formation so you can focus on the
                work, while the Agent handles the adherence."
              </p>
              <footer className="flex items-center gap-4" data-id="element-360">
                <div className="w-12 h-12 rounded-full bg-surface-300 border border-surface-200 flex items-center justify-center" data-id="element-361">
                  <span className="text-centurion-500 font-mono font-bold text-sm" data-id="element-362">
                    AG
                  </span>
                </div>
                <div data-id="element-363">
                  <cite className="text-gray-200 font-bold not-italic block" data-id="element-364">
                    Anil Gorti
                  </cite>
                  <span className="text-gray-500 text-sm" data-id="element-365">
                    21-time Marathoner & Product Leader
                  </span>
                </div>
              </footer>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="relative z-10 py-32 text-center px-6" data-id="element-366">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-centurion-500/5 rounded-full blur-[100px] pointer-events-none z-0" data-id="element-367"></div>
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="relative z-10 max-w-3xl mx-auto" data-id="element-368">
          <h2 className="font-mono text-3xl md:text-5xl font-bold text-gray-100 mb-6 uppercase tracking-tighter leading-tight" data-id="element-369">
            You're too expensive to be
            <br data-id="element-370" />
            your own Project Manager.
          </h2>
          <p className="text-lg text-gray-400 mb-12 max-w-xl mx-auto font-light" data-id="element-371">
            Let the Agent handle the adherence. You handle the work.
          </p>
          <Link to="/app" className="inline-flex bg-centurion-500 hover:bg-centurion-400 text-surface-600 px-10 py-5 rounded text-lg font-mono font-bold tracking-wide transition-all items-center justify-center gap-3 shadow-[0_0_30px_rgba(245,158,11,0.2)] hover:shadow-[0_0_50px_rgba(245,158,11,0.4)] hover:-translate-y-1" data-id="element-372">
            Apply for the Founding 50 Beta <ChevronRight size={22} data-id="element-373" />
          </Link>
        </motion.div>
      </section>

      {/* ===== PRIVACY ANCHOR ===== */}
      <section id="privacy" className="relative z-10 py-16 max-w-4xl mx-auto px-6" data-id="element-374">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="bg-surface-400/40 border border-surface-200 rounded-xl p-8 text-center" data-id="element-375">
          <h3 className="font-mono text-lg font-bold text-gray-200 mb-3 uppercase tracking-wide" data-id="element-376">
            Your Data. Your Control.
          </h3>
          <p className="text-sm text-gray-400 leading-relaxed max-w-2xl mx-auto" data-id="element-377">
            All biometric and calendar data is encrypted at rest and in transit.
            We never sell your data. You can export or delete everything at any
            time. SOC 2 Type II compliance in progress.
          </p>
        </motion.div>
      </section>
    </div>;
}